import * as THREE from 'three/webgpu';
import {
  Fn,
  attribute,
  cameraPosition,
  clamp,
  cos,
  dot,
  float,
  length,
  max,
  mix,
  mod,
  normalWorld,
  positionWorld,
  pow,
  sin,
  smoothstep,
  time,
  uniform,
  vec2,
  vec3,
} from 'three/tsl';
import { sampleTerrainHeight } from './terrainProfile';
import { DEFAULT_VISUAL_QUALITY_PRESET, type VisualQualityPreset } from './visualConfig';
import {
  DEFAULT_WORLD_THEME,
  type WorldThemeDefinition,
} from './worldTheme';

import { TslNode, hash21b } from './tslUtils';

// Sample a single material "tile" at a 2D projected position. Combines hash
// grain, striation, and pebble speckle.
function sampleMaterialTile(
  projected: TslNode,
  baseColor: TslNode,
  accentColor: TslNode,
  scale: TslNode,
): TslNode {
  const scaledUv = projected.mul(scale);
  const cell = scaledUv.floor();
  const grain = hash21b(cell);
  const striation = sin(scaledUv.x.add(scaledUv.y).mul(1.2)).mul(0.5).add(0.5);
  const pebbles = hash21b(cell.mul(0.5).add(scaledUv.mul(0.3).floor()));
  const base = baseColor.mul(0.88);
  const first = mix(base, accentColor, grain.mul(0.22));
  return mix(first, accentColor, striation.mul(0.08).add(pebbles.mul(0.05)));
}

// Triplanar blend: project the surface position on the three cardinal planes
// and weight by |normal|^5.
function triplanarMaterial(
  wp: TslNode,
  wn: TslNode,
  baseColor: TslNode,
  accentColor: TslNode,
  scale: TslNode,
): TslNode {
  // `pow(|n|, 5)` per-component. `wn.abs()` is vec3 at runtime, but TS sees
  // `any` through our loose typing and picks the scalar overload — use the
  // method chain `.pow(...)` to force per-component pow and keep swizzles
  // (`blend.x` etc.) typed as accessible properties.
  const rawBlend: TslNode = wn.abs().pow(vec3(5.0));
  const denom: TslNode = dot(rawBlend, vec3(1.0)).max(float(0.0001));
  const blend: TslNode = rawBlend.div(denom);
  const xAxis = sampleMaterialTile(wp.yz, baseColor, accentColor, scale);
  const yAxis = sampleMaterialTile(wp.xz, baseColor, accentColor, scale);
  const zAxis = sampleMaterialTile(wp.xy, baseColor, accentColor, scale);
  return xAxis.mul(blend.x).add(yAxis.mul(blend.y)).add(zAxis.mul(blend.z));
}

export class ProceduralTerrain {
  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshStandardNodeMaterial;

  constructor(
    scene: THREE.Scene,
    worldTheme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
    qualityPreset: VisualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET,
  ) {
    const geometry = new THREE.PlaneGeometry(
      worldTheme.terrain.size,
      worldTheme.terrain.size,
      worldTheme.terrain.segments,
      worldTheme.terrain.segments,
    );
    geometry.rotateX(-Math.PI / 2);

    const pos = geometry.attributes.position as THREE.BufferAttribute;
    const vertCount = pos.count;
    const heights = new Float32Array(vertCount);
    const slopes = new Float32Array(vertCount);

    for (let i = 0; i < vertCount; i++) {
      const px = pos.getX(i);
      const pz = pos.getZ(i);
      const h = sampleTerrainHeight(px, pz, worldTheme);
      pos.setY(i, h);
      heights[i] = h;
    }

    geometry.computeVertexNormals();

    const normals = geometry.attributes.normal as THREE.BufferAttribute;
    for (let i = 0; i < vertCount; i++) {
      slopes[i] = 1 - normals.getY(i);
    }

    geometry.setAttribute('aHeight', new THREE.BufferAttribute(heights, 1));
    geometry.setAttribute('aSlope', new THREE.BufferAttribute(slopes, 1));

    // ------------------------------------------------------------------
    // Uniforms / color inputs
    // ------------------------------------------------------------------
    // Theme-driven palette colors. Declared as local TSL uniforms so future
    // theme swaps could update them without rebuilding the node graph.
    const uGrassDark = uniform(new THREE.Color(worldTheme.terrain.colors.grassDark));
    const uGrassLight = uniform(new THREE.Color(worldTheme.terrain.colors.grassLight));
    const uDune = uniform(new THREE.Color(worldTheme.terrain.colors.dune));
    const uDirt = uniform(new THREE.Color(worldTheme.terrain.colors.dirt));
    const uRock = uniform(new THREE.Color(worldTheme.terrain.colors.rock));
    const uRockAccent = uniform(new THREE.Color(worldTheme.terrain.colors.rockAccent));
    const uCrystal = uniform(new THREE.Color(worldTheme.terrain.colors.crystal));
    const uCrystalAccent = uniform(new THREE.Color(worldTheme.terrain.colors.crystalAccent));
    const uFogColor = uniform(new THREE.Color(worldTheme.sky.fogColor));

    const uShorelineAngle = uniform(
      THREE.MathUtils.degToRad(worldTheme.terrain.shorelineAngleDeg),
    );
    const uShorelineOffset = uniform(worldTheme.terrain.shorelineOffset);
    const uInnerClearRadius = uniform(worldTheme.terrain.innerClearRadius);
    const uLandmarkRadius = uniform(worldTheme.terrain.landmarkRadius);
    const uShowContours = uniform(
      qualityPreset.features.terrainContours ? 1.0 : 0.0,
    );

    // Per-vertex attributes (written to geometry above). Cast to the loose
    // `TslNode` so downstream math chaining (`.mul`, `.min`, `mod()` etc.)
    // type-checks without fighting the generic `AttributeNode<string>` type.
    const aHeight: TslNode = attribute('aHeight', 'float');
    const aSlope: TslNode = attribute('aSlope', 'float');

    // ------------------------------------------------------------------
    // Shared masks Fn — `shore`, `outerWorld`, `crystalBand`. Packed into
    // a vec3 so both colorNode and emissiveNode can reuse the result.
    //   r = shore, g = outerWorld, b = crystalBand
    // ------------------------------------------------------------------
    const terrainMasks = Fn(() => {
      const wp = positionWorld;
      const slope = clamp(aSlope, 0.0, 1.0);

      const distFromCenter = length(vec2(wp.x, wp.z));
      // Shoreline directional band — `vec2(cos(a), sin(a))` in GLSL.
      const shoreDir = vec2(cos(uShorelineAngle), sin(uShorelineAngle));
      const signedDist = dot(vec2(wp.x, wp.z), shoreDir).sub(uShorelineOffset);
      const shore = smoothstep(float(-120.0), float(160.0), signedDist);

      const outerWorld = smoothstep(
        uInnerClearRadius.mul(0.9),
        uLandmarkRadius.add(90.0),
        distFromCenter,
      );
      const crystalBand = outerWorld
        .mul(shore)
        .mul(smoothstep(float(0.22), float(0.65), slope));

      return vec3(shore, outerWorld, crystalBand);
    });

    // ------------------------------------------------------------------
    // Main color graph (Fn) — mirrors the original fragment shader's layout.
    // We drop the hand-rolled diff/hemi/rim lighting because MeshStandard
    // lighting (scene Directional + Ambient + Hemisphere) now runs on top.
    // The additive crystal rim / caustic glow moves to `emissiveNode`.
    // ------------------------------------------------------------------
    const terrainColor = Fn(() => {
      const wp = positionWorld;
      const normal = normalWorld.normalize();
      const slope = clamp(aSlope, 0.0, 1.0);

      const masks = terrainMasks();
      const shore = masks.r;
      const crystalBand = masks.b;

      // Triplanar material samples for each palette slot.
      const grassMat = triplanarMaterial(wp.mul(0.055), normal, uGrassDark, uGrassLight, float(1.3));
      const duneMat = triplanarMaterial(wp.mul(0.05), normal, uDune, uDirt, float(1.05));
      const dirtMat = triplanarMaterial(wp.mul(0.065), normal, uDirt, uGrassLight, float(1.2));
      const rockMat = triplanarMaterial(wp.mul(0.045), normal, uRock, uRockAccent, float(1.35));
      const crystalMat = triplanarMaterial(wp.mul(0.08), normal, uCrystal, uCrystalAccent, float(1.8));

      // Slope-driven material mixing.
      const withDune = mix(grassMat, duneMat, shore.mul(0.35));
      const withDirt = mix(withDune, dirtMat, smoothstep(float(0.12), float(0.36), slope));
      const withRock = mix(withDirt, rockMat, smoothstep(float(0.34), float(0.8), slope));
      const baseColor = mix(withRock, crystalMat, crystalBand.mul(0.7));

      // Contour overlay — additive topo lines, gated by uShowContours (0 or 1).
      // Camera-relative fade keeps the overlay from drawing on distant terrain.
      const cameraDist = length(vec2(wp.x.sub(cameraPosition.x), wp.z.sub(cameraPosition.z)));
      const contourInterval = float(5.0);
      const contourWidth = float(0.12);
      const heightMod = mod(aHeight, contourInterval);
      const contourLine = smoothstep(float(0.0), contourWidth, heightMod.min(contourInterval.sub(heightMod))).oneMinus();
      const distanceFade = smoothstep(float(80.0), float(200.0), cameraDist).oneMinus();
      const majorInterval = contourInterval.mul(5.0);
      const majorMod = mod(aHeight, majorInterval);
      const majorLine = smoothstep(float(0.0), contourWidth.mul(1.5), majorMod.min(majorInterval.sub(majorMod))).oneMinus();
      const contourColor = vec3(0.28, 0.42, 0.22);
      const contourAlpha = contourLine
        .mul(0.35)
        .max(majorLine.mul(0.6))
        .mul(distanceFade)
        .mul(smoothstep(float(0.8), float(0.4), slope))
        .mul(uShowContours);
      const withContours = baseColor.add(contourColor.mul(contourAlpha).mul(2.0));

      // Manual exponential-ish fog (smoothstep 340-1220, max 68%) — material
      // uses `fog: false` so we control the curve here.
      const fog = smoothstep(float(340.0), float(1220.0), cameraDist);
      return mix(withContours, uFogColor, fog.mul(0.68));
    });

    // ------------------------------------------------------------------
    // Emissive — caustic shimmer + crystal rim glow. Both are camera/time
    // driven, so they belong in emissiveNode (MRT-selective-bloom target).
    // ------------------------------------------------------------------
    const terrainEmissive = Fn(() => {
      const wp = positionWorld;
      const normal = normalWorld.normalize();
      const slope = clamp(aSlope, 0.0, 1.0);

      const masks = terrainMasks();
      const shore = masks.r;
      const outerWorld = masks.g;
      const crystalBand = masks.b;

      // Caustic shimmer near the shore: sin/sin cross-pattern, driven by uTime.
      const causticMask = shore
        .mul(outerWorld)
        .mul(smoothstep(float(0.18), float(0.5), slope).oneMinus());
      const causticWave = sin(wp.x.mul(0.11).add(time.mul(0.45)))
        .mul(sin(wp.z.mul(0.09).sub(time.mul(0.35))));
      const caustic = causticMask.mul(causticWave);

      // Fresnel rim that picks up crystal color along the outer-world steep slopes.
      const viewDir = cameraPosition.sub(wp).normalize();
      const rim = pow(max(dot(normal, viewDir), float(0.0)).oneMinus(), float(4.0));

      return uCrystalAccent.mul(caustic.mul(0.04).add(rim.mul(crystalBand).mul(0.18)));
    });

    // ------------------------------------------------------------------
    // Material
    // ------------------------------------------------------------------
    this.material = new THREE.MeshStandardNodeMaterial({
      flatShading: false,
      fog: false, // Manual fog in colorNode preserves the original curve.
    });
    this.material.colorNode = terrainColor();
    this.material.emissiveNode = terrainEmissive();
    this.material.roughnessNode = float(0.95);
    this.material.metalnessNode = float(0.0);

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.position.y = worldTheme.terrain.baseY;
    this.mesh.receiveShadow = false;
    scene.add(this.mesh);
  }

  /**
   * Called every frame from ArenaRenderer. TSL's built-in `time` node and
   * `cameraPosition` accessor handle their own updates; the scene's
   * DirectionalLight (driven by SceneManager.sunPosition) handles sun-direction
   * lighting. Parameters are kept for API compatibility with ArenaRenderer.
   */
  update(
    _deltaMs: number,
    _sunDirection: THREE.Vector3,
    _cameraPosition: THREE.Vector3,
  ): void {
    // TSL `time` node auto-updates; no manual accumulation needed.
  }

  /** Toggle wireframe debug overlay (unchanged from the WebGL path). */
  setWireframe(enabled: boolean): void {
    this.material.wireframe = enabled;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
